import { navigate } from '@reach/router';
import className from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bulma-components';
import ContentLoader from 'react-content-loader';
import { ROLES } from 'services/Constant';
import { organizationService } from 'services/organizationService';

import './organizationsTableEntry.scss';

const { Input, Control } = Form;

function OrganizationsTableEntry({
    organizationData,
    parentId,
    setErrors,
    setSuccess,
    smallName,
    parentLimits,
    allChildOrganizations,
    onUpdate
}) {
    const isAdmin = useMemo(() => {
        const result = organizationData?.roles?.some((roleId) => ROLES.admin.id === roleId);
        return result;
    }, [organizationData]);
    const [isInEdit, setIsInEdit] = useState(false);
    const [updating, setUpdating] = useState(undefined);
    const [validationErrors, setValidationErrors] = useState({});
    const [limitInputs, setLimitInputs] = useState({});

    const [organization, setOrganization] = useState();

    const validateLimit = (fieldName, newValue, currentOrgId) => {
        if (!parentLimits || !allChildOrganizations) {
            return null;
        }

        const parentLimit = parentLimits[fieldName];
        if (parentLimit === undefined || parentLimit === null) {
            return null;
        }

        // Calculate sum of all child organizations except the current one being edited
        const sumOfOthers = allChildOrganizations
            .filter((child) => child.id !== currentOrgId)
            .reduce((sum, child) => sum + (parseInt(child[fieldName]) || 0), 0);

        const totalWithNewValue = sumOfOthers + (parseInt(newValue) || 0);

        if (totalWithNewValue > parentLimit) {
            const available = parentLimit - sumOfOthers;
            const errorMsg = `This value must be less than ${available + 1}.`;
            return errorMsg;
        }

        return null;
    };

    useEffect(() => {
        const data = organizationData;
        if (isNaN(data.mdc_limit_analysis)) {
            // eslint-disable-next-line camelcase
            data.mdc_limit_analysis = 0;
        }
        setOrganization(data);
    }, [organizationData]);

    const handleChange = (event) => {
        const { name, value } = event.target;

        // Always update the input value to allow typing
        setLimitInputs((prev) => ({ ...prev, [name]: value }));

        // Validate the input
        const error = validateLimit(name, value, organization?.id);

        setValidationErrors((prev) => {
            const newErrors = {
                ...prev,
                [name]: error
            };
            return newErrors;
        });
    };

    const modalEditOrg = useMemo(() => {
        const addHandler = () => {
            // Check if there are any validation errors
            const hasErrors = Object.values(validationErrors).some((error) => error !== null && error !== undefined);

            if (hasErrors) {
                return;
            }

            // Process limitInputs - convert to integers or use organization defaults
            const processedLimits = {};
            for (const key in limitInputs) {
                const parsedValue = parseInt(limitInputs[key]);
                if (isNaN(parsedValue) || limitInputs[key] === null || limitInputs[key] === '') {
                    processedLimits[key] = organization[key];
                } else {
                    processedLimits[key] = parsedValue;
                }
            }

            if (organization && parentId) {
                setUpdating({ updating: organization.name });
                (async () => {
                    const newOrgData = {
                        preventionApiLimit: processedLimits.preventionApiLimit,
                        reputationApiLimit: processedLimits.reputationApiLimit,
                        sandboxApiLimit: processedLimits.sandboxApiLimit,
                        dailyFeedLimit: processedLimits.dailyFeedLimit,
                        // eslint-disable-next-line camelcase
                        mdc_limit_analysis: processedLimits.mdc_limit_analysis,
                        // eslint-disable-next-line camelcase
                        parent_id: parentId
                    };

                    const result = await organizationService.updateSubOrganization(organization.id, {
                        organization: { ...newOrgData }
                    });
                    if (result.error) {
                        // Add fail
                        setErrors(result.messages);
                    } else {
                        setSuccess([`Licenses for '${organization.name}' updated successfully!`]);
                        const updatedOrg = {
                            ...organization,
                            ...newOrgData
                        };
                        setOrganization(updatedOrg);

                        // Notify parent to update the child organizations array
                        if (onUpdate) {
                            onUpdate(organization.id, updatedOrg);
                        }
                    }
                    setUpdating(undefined);
                })();
            }
            setIsInEdit(false);
        };

        const testKeyIsPositiveNumber = (event) => {
            if (!/\d/.test(event.key)) {
                event.preventDefault();
            }

            return undefined;
        };

        const hasErrors = Object.values(validationErrors).some((error) => error !== null && error !== undefined);

        const addBtn = (
            <a
                className='addBtn'
                onClick={hasErrors ? undefined : addHandler}
                style={{
                    cursor: hasErrors ? 'not-allowed' : 'pointer',
                    opacity: hasErrors ? 0.5 : 1,
                    pointerEvents: hasErrors ? 'none' : 'auto'
                }}
            >
                <i className='fas fa-check' style={{ color: hasErrors ? '#ccc' : 'inherit' }} />
            </a>
        );

        const cancelBtn = (
            <a
                className='cancelBtn'
                onClick={() => {
                    setIsInEdit(false);
                    setValidationErrors({});
                }}
            >
                <i className='fas fa-ban' />
            </a>
        );

        if (organization) {
            let shortOrganizationName =
                organization.name.length > 10 ? organization.name.slice(0, 10) + '...' : organization.name;

            // Define the fields to display
            const limitFields = [
                'preventionApiLimit',
                'reputationApiLimit',
                'sandboxApiLimit',
                'dailyFeedLimit',
                'mdc_limit_analysis'
            ];

            // Calculate available amounts for each field
            const availableAmounts = {};
            limitFields.forEach((fieldName) => {
                if (parentLimits && allChildOrganizations) {
                    const parentLimit = parentLimits[fieldName];
                    if (parentLimit !== undefined && parentLimit !== null) {
                        const sumOfOthers = allChildOrganizations
                            .filter((child) => child.id !== organization.id)
                            .reduce((sum, child) => sum + (parseInt(child[fieldName]) || 0), 0);
                        availableAmounts[fieldName] = Math.max(0, parentLimit - sumOfOthers);
                    } else {
                        availableAmounts[fieldName] = null;
                    }
                } else {
                    availableAmounts[fieldName] = null;
                }
            });

            return (
                <tr>
                    <td style={{ verticalAlign: 'top' }}>
                        <div className='entryContainer'>
                            <p>{smallName ? `${shortOrganizationName}` : `${organization.name}`}</p>
                            {hasErrors && (
                                <p
                                    className='help is-danger'
                                    style={{
                                        fontSize: '12px',
                                        marginTop: '0.25rem',
                                        textAlign: 'left',
                                        whiteSpace: 'pre-wrap'
                                    }}
                                >
                                    Total requests of sub-organizations exceeds the organization's requests.
                                </p>
                            )}
                        </div>
                    </td>
                    {limitFields.map((key, index) => {
                        const isDisabled = availableAmounts[key] === 0;
                        return (
                            <td key={index} style={{ verticalAlign: 'top' }}>
                                <Control className='editInput'>
                                    <Input
                                        className={className({ invalidInput: validationErrors[key] })}
                                        value={limitInputs[key] ?? ''}
                                        onChange={handleChange}
                                        onKeyPress={(e) =>
                                            e.key === 'Enter' ? addHandler() : testKeyIsPositiveNumber(e)
                                        }
                                        name={`${key}`}
                                        type='text'
                                        placeholder={`${organization[key]}`}
                                        autoFocus={index === 0}
                                        disabled={isDisabled}
                                    />
                                    {validationErrors[key] && (
                                        <p
                                            className='help is-danger'
                                            style={{
                                                fontSize: '12px',
                                                marginTop: '0.25rem',
                                                textAlign: 'left',
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word'
                                            }}
                                        >
                                            {validationErrors[key]}
                                        </p>
                                    )}
                                </Control>
                            </td>
                        );
                    })}
                    <td style={{ verticalAlign: 'top' }}>
                        <div className='entryActionContainer'>
                            {addBtn}
                            {cancelBtn}
                        </div>
                    </td>
                </tr>
            );
        }
    }, [
        organization,
        parentId,
        updating,
        smallName,
        validationErrors,
        limitInputs,
        handleChange,
        parentLimits,
        allChildOrganizations,
        onUpdate
    ]);

    const goToOrgButton = (organizationId) => {
        if (isAdmin) {
            const addHandler = async () => {
                await navigate(`/organization/${organizationId}/overview`);
            };

            return (
                <a onClick={addHandler}>
                    <i className='fas fa-arrow-right' />
                </a>
            );
        }
        return null;
    };

    const editOrgButton = useMemo(() => {
        if (isAdmin) {
            const addHandler = () => {
                setErrors();
                setSuccess();
                setValidationErrors({});
                setLimitInputs({
                    preventionApiLimit: null,
                    reputationApiLimit: null,
                    sandboxApiLimit: null,
                    dailyFeedLimit: null,
                    // eslint-disable-next-line camelcase
                    mdc_limit_analysis: null
                });
                setIsInEdit(true);
            };
            return (
                <a className='editOrgBtn' onClick={addHandler}>
                    <i className='fas fa-pen' />
                </a>
            );
        }

        return null;
    }, [isAdmin, isInEdit]);

    const dom = useMemo(() => {
        if (updating) {
            return (
                <tr>
                    <td colSpan={6}>
                        <ContentLoader height={20} width='100%' speed={1}>
                            <rect x='0' y='0' rx='10' ry='10' width={`${40 + 60 * Math.random()}%`} height='20' />
                        </ContentLoader>
                    </td>
                </tr>
            );
        }

        if (isInEdit && organization) {
            return modalEditOrg;
        } else if (organization) {
            let shortOrganizationName =
                organization.name.length > 10 ? organization.name.slice(0, 10) + '...' : organization.name;
            return (
                <tr>
                    <td style={{ verticalAlign: 'top' }}>
                        {smallName ? `${shortOrganizationName}` : `${organization.name}`}
                    </td>
                    <td style={{ verticalAlign: 'top' }}>{`${organization.preventionApiLimit}`}</td>
                    <td style={{ verticalAlign: 'top' }}>{`${organization.reputationApiLimit}`}</td>
                    <td style={{ verticalAlign: 'top' }}>{`${organization.sandboxApiLimit}`}</td>
                    <td style={{ verticalAlign: 'top' }}>{`${organization.dailyFeedLimit}`}</td>
                    <td style={{ verticalAlign: 'top' }}>{`${organization.mdc_limit_analysis}`}</td>
                    <td style={{ verticalAlign: 'top' }}>
                        {editOrgButton}
                        {goToOrgButton(organization.id)}
                    </td>
                </tr>
            );
        }

        return <></>;
    }, [organization, isInEdit, updating, smallName, editOrgButton, modalEditOrg]);

    return dom;
}

export default OrganizationsTableEntry;

OrganizationsTableEntry.propTypes = {
    organizationData: PropTypes.object,
    parentId: PropTypes.string,
    setErrors: PropTypes.func,
    setSuccess: PropTypes.func,
    smallName: PropTypes.bool,
    parentLimits: PropTypes.object,
    allChildOrganizations: PropTypes.array,
    onUpdate: PropTypes.func
};
